import {useState, useEffect} from "react";
import {useLocation, useHistory} from "react-router-dom";
import {useConfig} from '../ConfigProvider';
import {useTunnelData} from "../TunnelContext";
import baseApi from "../baseApi";
import OAButton from "../components/OAButton";
import CustomInput from "../components/CustomInput";
import { Search } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import OALogo from "../components/OALogo";
import Map from "../components/Map";
import {useTranslation} from "react-i18next";
import PromoBanner from "../components/PromoBanner";
import {GeoLocationIcon, LocationIcon,PositionIcon} from "../components/icons";
import SearchInput from "../components/SearchInput";

import Footer from "../components/Footer";
import pushEvent from '../pushEvent'

export default function ClubSelection(){
	const mql = window.matchMedia("(max-width: 600px)");
	const isMobile = mql.matches;
	const [hide, hideSet] = useState(true);
	const {search} = useLocation();
	const history = useHistory();
	const config = useConfig();
	const clubs = config.places.values;
	const [sortedClubs, sortedClubsSet] = useState(clubs);
	const {tunnel, setTunnelClub, setTunnelUtm} = useTunnelData();
	const [departmentOrPosition, departmentOrPositionSet] = useState({type:null});
	const [showMap, setShowMap] = useState(!isMobile);
	const [currentLocalisation,setCurrentLocalisation] = useState(null)
	const {t} = useTranslation()

	useEffect(() => {
		window.scrollTo(0, 0);
		pushEvent({
			'event' : 'funnel_step_1',
			'step_name' : 'Mon Club'
		});
	}, []);


	const sortByNear = (loc)=>{
		const sorted = [...clubs];
		sorted.forEach( s => {
			s.dist = dist(s.lat, s.long, loc.lat, loc.long);
		})
		let x = sorted.sort( (a,b) => a.dist - b.dist)
		sortedClubsSet(sorted);
	}

	function containsAny(mainString, subStrings) {
		for (let i = 0; i < subStrings.length; i++) {
			if (mainString.includes(subStrings[i])) {
				return true;
			}
		}
		return false;
	}

	const sortBySubstring = (arr, substring) => {
		return arr.sort((a, b) => {
			const aContains = a.includes(substring);
			const bContains = b.includes(substring);

			if (aContains && !bContains) return -1;
			if (!aContains && bContains) return 1;
			return 0;
		});
	};



	useEffect( () => {
		const searchParams = new URLSearchParams(search);
		if (searchParams.get('utm_source') || searchParams.get('utm_medium') || searchParams.get('utm_campaign')) {
			setTunnelUtm(searchParams.get('utm_source'), searchParams.get('utm_medium'), searchParams.get('utm_campaign'))
		}

		const msdsId = searchParams.get('msdsId');
		if( !msdsId ) {
			hideSet(false);
			return;
		}
		const found = clubs.find( c => c.msdsId == msdsId );
		if( !found ) {
			hideSet(false);
			return;
		}
		setTunnelClub(found);
		history.push('/plans');
	}, []);

	const hav = (x) => {
		const s = Math.sin(x / 2)
		return s * s
	}

	function dist(lat1, lon1, lat2, lon2) {
		const R = 6371; // Radius of the earth in km
		const dLat = deg2rad(lat2-lat1);		// deg2rad below
		const dLon = deg2rad(lon2-lon1); 
		const a = 
			Math.sin(dLat/2) * Math.sin(dLat/2) +
			Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
			Math.sin(dLon/2) * Math.sin(dLon/2)
		;
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
		const d = R * c; // Distance in km
		return d;
	}

	function deg2rad(deg) {
		return deg * (Math.PI/180)
	}


	useEffect( () => {
		if( !clubs )
			return;
		if( departmentOrPosition?.type === 'department' ) {
			sortedClubsSet( clubs.filter( c => c.zip.startsWith(departmentOrPosition.value) ));
			return;
		}
		if( departmentOrPosition?.type === 'geoloc' ) {
			const sorted = [...clubs];
			sorted.forEach( s => {
				s.dist = dist(s.lat, s.long, departmentOrPosition.value.lat, departmentOrPosition.value.lng);
			})
			sorted.sort( (a,b) => a.dist - b.dist);
			sortedClubsSet(sorted);
			return;
		}
		sortedClubsSet(clubs);
	}, [clubs, departmentOrPosition]);

	
	if( hide )
		return null;

	let filtered = sortedClubs;

	//Used to center the map
	let mapPosition = null;
	if( tunnel.club ) {
		mapPosition = [ tunnel.club.lat, tunnel.club.long ]
	} else if( departmentOrPosition?.type === 'geoloc' ) {
		mapPosition = [departmentOrPosition.value.lat, departmentOrPosition.value.lng]
	}

	const nextUrl = (club) => {
		const currentUrl = new URL(document.location);
		currentUrl.searchParams.set('msdsId', `${(club || tunnel.club)?.msdsId}`)
		currentUrl.searchParams.sort();
		return `/plans?${currentUrl.searchParams.toString()}`
	};

	const onChooseClub = (c) => {
		const clubName = c.name;
		const cNameToUrl = (clubName+"").toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, "").replaceAll(' ', '-');
		const clubUrl = `${config.baseSite}/club/${cNameToUrl}`;
		window.location = clubUrl;
	};

	return (
		<>
		<div className="bg-oaBackgroundColor animate-fade-in-left w-full">
			<section className="w-full px-5 pt-4 md:px-0 md:pt-10">
				<OALogo className={"animate-fade-in"} />
				<PromoBanner />

				<div className="my-10 md:my-16">
					<Breadcrumbs />
				</div>
				<div className="w-full text-center py-10">
					<h2 className="text-4xl lg:text-[50px] font-bold whitespace-nowrap sm:whitespace-normal">
						{t("club_selection.title.chose").toUpperCase()} <span className="block sm:inline">{t("club_selection.title.your_club").toUpperCase()}</span>
					</h2>
				</div>
				<div className="lg:hidden">
					<button 
						className={`flex gap-0.5 text-xs font-extrabold italic md:hidden text-oaForegroundText mb-4`}
						onClick={() =>{
								setShowMap(!showMap)
						}}
						>
							<LocationIcon className={"size-4"}/>
							{showMap ? 
								<p>{t("club_selection.map.toggle.hide")}</p>
					: 
								<p>{t("club_selection.map.toggle.show")}</p>
							}
					</button>
					{showMap ? 
						<div>
							<Map club={tunnel?.club} clubs={sortedClubs} className="w-full h-96 " center={mapPosition}/>
							<div className="flex gap-2 mt-2">
								<div className="flex gap-1 items-center">
									<PositionIcon className={"size-3 fill-oaRed"}/>
									<p className="text-xs">{t("club_selection.map.legend.open_club")}</p>
								</div>
								<div className="flex gap-1 items-center">
									<PositionIcon className={"size-3"}/>
									<p className="text-xs">{t("club_selection.map.legend.pre_open_club")}</p>
								</div>
							</div>
						</div>

					: 
						null
					}
				</div>

				<SearchInput
					showMap={showMap}
					geoChange={ p => departmentOrPositionSet({type:'geoloc', value:p})}
					departmentChange={ d=> {
						if( !d )
							return departmentOrPositionSet(null);
						departmentOrPositionSet({type:'department', value:d});
					}}
				/>

				<div className="flex w-full lg:h-[600px] justify-center ">
					<div className="h-full lg:w-7/12 xl:w-5/12 lg:bg-[#131614] lg:py-8 lg:px-6">
						<div className="custom-scrollbar lg:overflow-y overflow-x-hidden h-full">
							<ClubList 
								clubs={sortedClubs} 
								onSelect={(club) => {
									setTunnelClub(club);
									history.push(nextUrl(club));
								}}
								onCardClick={(club) => {
									setTunnelClub(club)
								}}
							/>
						</div>
					</div>
					<div className="relative lg:w-5/12 xl:w-7/12 h-full hidden lg:block">
						<Map club={tunnel?.club} clubs={clubs} className="w-full h-full" onSelect={c => {
							setTunnelClub(c);
						}} onChooseClub={onChooseClub}/>
						<div className="z-[999] absolute flex flex-col gap-2 mt-2 bottom-5 right-2">
							<div className="flex gap-1 items-center">
								<PositionIcon className={"size-3 fill-oaRed"}/>
								<p className="text-xs">{t("club_selection.map.legend.open_club")}</p>
							</div>
							<div className="flex gap-1 items-center">
								<PositionIcon className={"size-3 fill-white"}/>
								<p className="text-xs">{t("club_selection.map.legend.pre_open_club")}</p>
							</div>
						</div>
					</div>
				</div>
			</section>
		</div>
		<footer className="w-full md:hidden lg:block">
			<Footer></Footer>
		</footer>
		</>
	);
}

function ClubList({clubs, onSelect, onCardClick}){
	const {t} = useTranslation()
	return (
		<ol>
			{clubs.map( (club, index)=>{
				return (
					<div className="group animate-fade-in-down" key={index}>
						<li className="w-full mb-2 px-4 py-4 rounded-[8px] hover:bg-oaHoverCard lg:hover:bg-oaForegroundText hover:cursor-pointer transition-all duration-300	" onClick={() => onCardClick(club)}>
						<article className="flex flex-col lg:flex-row items-center gap-4 lg:gap-8">
							<div className="w-full h-[300px] md:h-[350px] xl:w-auto lg:h-[150px]">
								{club.imgUrl && <img className="w-full h-full lg:w-60 lg:h-[150px] object-cover rounded-[4px]" src={club.imgUrl} alt={club.name}/>}
							</div>
							<section className="w-full flex flex-col items-center lg:items-start xl:flex-1">
								<h3 className="text-xl lg:text-oaBackgroundText">{club.address}</h3>
								<address className="w-full lg:text-white text-sm not-italic flex justify-center lg:justify-start items-center gap-1 my-3	">
									<div className="flex gap-1">
										<LocationIcon className={"fill-oaRed size-4"}/>
										{club.dist && <p>{Math.round(club.dist)} km,</p>}
									</div>
									<div>{club.zip}, {club.city}</div>
								</address>
								<div className=" flex gap-4 lg:gap-1 xl:gap-4 xl:flex-row-reverse">
									<OAButton className={"lg:border-none lg:bg-oaRed lg:group-hover:bg-oaBlack lg:hover:bg-oaBlack text-nowrap lg:text-white font-bold border-[2px] rounded-[5px] bg-transparent border-2 border-oaRed hover:bg-oaRed/20 text-oaRed"} onClick={()=>onSelect(club)} >{t("club_selection.club_card.select_club").toUpperCase()}</OAButton>
									<OAButton className={"lg:border-none lg:bg-oaForegroundText text-nowrap lg:text-white border-[2px] border-[#2D2D2D] rounded-[5px] bg-transparent border-2 border-oaBackgroundText hover:bg-white hover:text-black text-oaForegroundText"} onClick={() => onCardClick(club)}>{t("club_selection.club_card.show_club").toUpperCase()}</OAButton>
								</div>
							</section>
						</article>
						
						</li>
						{clubs.length-1>index ? 
							<li className="hidden xl:block w-full h-[1px] bg-oaBackgroundText my-4"></li>
						:null}
					</div>
				);
			})}
		</ol>
	);
}

function GeoSearch({geoChange, departmentChange}){
	const [geoActive, geoActiveSet] = useState(false);
	const [zipCode, zipCodeSet] = useState('');
	const [zipCodeError, zipCodeErrorSet] = useState(false);
	const {t} = useTranslation();

	const searchByZip = () => {
		if (zipCode === ''){
			departmentChange(null);
			return;
		}
		zipCodeErrorSet(false);
		if( zipCode.length <= 4 ) {
			departmentChange(zipCode);
			return;
		}
		const [query] = baseApi.GET({
			path: "/search",
			origin: "https://nominatim.openstreetmap.org",
			query: {postalcode: zipCode, format: 'jsonv2', country: 'France'},
			headers: {'content-type': 'application/json', 'access-control-allow-methods': 'OPTIONS,GET', 'access-control-allow-origin': '*'}
		})
		query
		.then(results => {
			if (results.length < 1) {
				zipCodeErrorSet(true)
				console.error("Error on finding location by zipCode:", zipCode);
				return;
			}
			geoChange({lat: +(results[0].lat), lng: +(results[0].lon)});
		})
		.catch( e => {
			console.error("Cannot search by zip", e)
			zipCodeErrorSet(true);
		})
	}

	const searchByGeoloc = () => {
		if (! ("geolocation" in navigator) ) {
			console.error("Geolocation is not available");
			return;
		}
		!geoActive && geoActiveSet(true)
		new Promise((resolve, reject) => {
			navigator.geolocation.getCurrentPosition((pos) => {
				zipCodeSet('');
				zipCodeErrorSet(false)
				geoChange({lat: pos.coords.latitude, lng: pos.coords.longitude})
			}, reject);
		})
	};

	return (
		<form className='GeoSearch' onSubmit={(e) => {e.preventDefault(); searchByZip(zipCode)}}>
			<fieldset className={!!zipCodeError ? "error" : ""}>
				<div className="text-base font-medium mb-2">Saisir la ville du club</div>
				<CustomInput 
					className="mb-6 placeholder:text-oaPlaceholderText outline-none focus:ring-2 focus:ring-oaRed/20"
					type="text" 
					value={zipCode} 
					onChange={ e=>{zipCodeSet(e.target.value.trim()); zipCodeErrorSet(false)}} 
					placeholder="Rechercher un club" 
					autoComplete="postal-code" 
					pattern="[0-9]*"
					inputAdornment={<Search className="size-5 text-oaPlaceholderText"/>}
				/>
				<button onClick={searchByGeoloc}>
					{geoActive
						? <p>geo active</p>
						: <p>geo inactive</p>
					}
				</button> 
			</fieldset>
			<button className="mb-4" onClick={ () => searchByZip(zipCode)}><span>{t("search")}</span></button>
		</form>
	);
}
